const DEFAULT_BUZZER_GAIN = 0.03;
const DEFAULT_FREQUENCY_HZ = 440;
const GAIN_RAMP_SECONDS = 0.02;
const VOICE_RELEASE_DELAY_MS = 120;
const MIN_OUTPUT_FREQUENCY_HZ = 20;
const MAX_OUTPUT_FREQUENCY_HZ = 20000;

const clamp_frequency_hz = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.min(MAX_OUTPUT_FREQUENCY_HZ, Math.max(MIN_OUTPUT_FREQUENCY_HZ, numericValue));
};

const get_audio_context_constructor = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.AudioContext || window.webkitAudioContext || null;
};

const create_buzzer_voice = (audio_context) => {
  const oscillator = audio_context.createOscillator();
  const gain_node = audio_context.createGain();

  oscillator.type = 'square';
  oscillator.frequency.setValueAtTime(DEFAULT_FREQUENCY_HZ, audio_context.currentTime);
  gain_node.gain.setValueAtTime(0, audio_context.currentTime);
  oscillator.connect(gain_node);
  gain_node.connect(audio_context.destination);
  oscillator.start();

  return {
    gain_node,
    oscillator,
    release_timeout_id: null,
  };
};

export const create_passive_buzzer_audio_manager = ({ default_gain = DEFAULT_BUZZER_GAIN } = {}) => {
  let audio_context = null;
  const voices_by_component_id = new Map();

  const ensure_audio_context = () => {
    if (audio_context && audio_context.state !== 'closed') {
      return audio_context;
    }

    const AudioContextConstructor = get_audio_context_constructor();

    if (!AudioContextConstructor) {
      return null;
    }

    audio_context = new AudioContextConstructor();
    return audio_context;
  };

  const release_voice = (component_id, immediate = false) => {
    const voice = voices_by_component_id.get(component_id);
    const context = audio_context;

    if (!voice || !context) {
      voices_by_component_id.delete(component_id);
      return;
    }

    if (voice.release_timeout_id !== null) {
      window.clearTimeout(voice.release_timeout_id);
      voice.release_timeout_id = null;
    }

    const stop_voice = () => {
      try {
        voice.oscillator.stop();
      } catch {}

      try {
        voice.oscillator.disconnect();
      } catch {}

      try {
        voice.gain_node.disconnect();
      } catch {}

      voices_by_component_id.delete(component_id);
    };

    if (immediate) {
      stop_voice();
      return;
    }

    const now = context.currentTime;
    voice.gain_node.gain.cancelScheduledValues(now);
    voice.gain_node.gain.setValueAtTime(voice.gain_node.gain.value, now);
    voice.gain_node.gain.linearRampToValueAtTime(0, now + GAIN_RAMP_SECONDS);
    voice.release_timeout_id = window.setTimeout(stop_voice, VOICE_RELEASE_DELAY_MS);
  };

  const get_or_create_voice = (component_id) => {
    const existing_voice = voices_by_component_id.get(component_id);

    if (existing_voice) {
      if (existing_voice.release_timeout_id !== null) {
        window.clearTimeout(existing_voice.release_timeout_id);
        existing_voice.release_timeout_id = null;
      }

      return existing_voice;
    }

    const context = ensure_audio_context();

    if (!context) {
      return null;
    }

    const next_voice = create_buzzer_voice(context);
    voices_by_component_id.set(component_id, next_voice);
    return next_voice;
  };

  return {
    async ensure_audio_context_unlocked() {
      const context = ensure_audio_context();

      if (!context) {
        return false;
      }

      if (context.state === 'suspended') {
        try {
          await context.resume();
        } catch {
          return false;
        }
      }

      return context.state === 'running';
    },

    set_passive_buzzer_tone(component_id, is_active, frequency_hz) {
      const normalized_component_id = String(component_id || '').trim();

      if (!normalized_component_id) {
        return;
      }

      if (!is_active) {
        release_voice(normalized_component_id);
        return;
      }

      const clamped_frequency_hz = clamp_frequency_hz(frequency_hz);

      if (!Number.isFinite(clamped_frequency_hz)) {
        release_voice(normalized_component_id);
        return;
      }

      const voice = get_or_create_voice(normalized_component_id);
      const context = audio_context;

      if (!voice || !context) {
        return;
      }

      const now = context.currentTime;
      voice.oscillator.frequency.cancelScheduledValues(now);
      voice.oscillator.frequency.setTargetAtTime(clamped_frequency_hz, now, 0.01);
      voice.gain_node.gain.cancelScheduledValues(now);
      voice.gain_node.gain.setValueAtTime(voice.gain_node.gain.value, now);
      voice.gain_node.gain.linearRampToValueAtTime(default_gain, now + GAIN_RAMP_SECONDS);
    },

    stop_passive_buzzer(component_id) {
      const normalized_component_id = String(component_id || '').trim();

      if (!normalized_component_id) {
        return;
      }

      release_voice(normalized_component_id);
    },

    stop_all_buzzers() {
      Array.from(voices_by_component_id.keys()).forEach((component_id) => {
        release_voice(component_id);
      });
    },

    async dispose() {
      Array.from(voices_by_component_id.keys()).forEach((component_id) => {
        release_voice(component_id, true);
      });

      if (audio_context && audio_context.state !== 'closed') {
        try {
          await audio_context.close();
        } catch {}
      }

      audio_context = null;
    },
  };
};
